import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Pencil, Trash2, Flame, Minus, Plus, EyeOff } from "lucide-react";
import { toast } from "sonner";
import ProductForm from "@/components/app/ProductForm";
import { fullUrl } from "@/components/app/ImageUploader";
import { brl, categoryLabel, fmtDate, fmtDateTime, parsePrice, priceInput } from "@/lib/adega";

function ProductRow({ p, onPatch, onEdit, onDelete, onQuickPrice }) {
  return (
    <li className={`rounded-xl border p-3 ${p.promo ? "border-primary/60 bg-primary/5" : "border-white/10 bg-card"} ${p.hidden ? "opacity-60" : ""}`} data-testid={`product-${p.product_id}`}>
      <div className="flex items-start gap-3">
        {p.photo_url && <img src={fullUrl(p.photo_url)} alt="" className="h-12 w-12 rounded-lg object-cover shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className={`text-sm font-semibold truncate ${p.out_of_stock ? "line-through text-muted-foreground" : ""}`}>{p.name} {p.hidden && <EyeOff className="inline w-3.5 h-3.5" />}</div>
          <div className="text-xs text-muted-foreground truncate">{categoryLabel(p.category)}{p.description ? ` · ${p.description}` : ""}</div>
          <div className="text-[10px] text-muted-foreground">Preço atualizado em {fmtDate(p.price_updated_at)}{p.promo ? ` · promo ${brl(p.promo_price)} até ${fmtDateTime(p.promo_until)}` : ""}</div>
        </div>
        <button onClick={() => onQuickPrice(p)} className="h-11 px-3 rounded-lg bg-primary text-primary-foreground font-bold text-sm press shrink-0" data-testid={`product-quick-price-${p.product_id}`}>
          {brl(p.price)}
        </button>
      </div>
      <div className="flex items-center gap-4 mt-3 text-xs flex-wrap">
        <label className="flex items-center gap-2"><Switch checked={p.out_of_stock} onCheckedChange={(v) => onPatch(p, { out_of_stock: v })} data-testid={`product-stock-${p.product_id}`} /> Em falta</label>
        <label className="flex items-center gap-2"><Switch checked={p.hidden} onCheckedChange={(v) => onPatch(p, { hidden: v })} data-testid={`product-hidden-${p.product_id}`} /> Esconder</label>
        {p.promo && <span className="flex items-center gap-1 text-primary"><Flame className="w-3.5 h-3.5" /> Promo</span>}
        <span className="flex-1" />
        <button onClick={() => onEdit(p)} className="h-9 w-9 grid place-items-center text-muted-foreground press" data-testid={`product-edit-${p.product_id}`}><Pencil className="w-4 h-4" /></button>
        <button onClick={() => onDelete(p)} className="h-9 w-9 grid place-items-center text-destructive press" data-testid={`product-delete-${p.product_id}`}><Trash2 className="w-4 h-4" /></button>
      </div>
    </li>
  );
}

function QuickPrice({ p, onSave, onClose }) {
  const [v, setV] = useState(priceInput(p?.price));
  const step = (d) => setV(priceInput(Math.max(0, Math.round(((parsePrice(v) || 0) + d) * 100) / 100)));
  return (
    <Dialog open={!!p} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="z-[1600] bg-card border-white/10" data-testid="quick-price-dialog">
        <DialogHeader><DialogTitle>{p?.name}</DialogTitle></DialogHeader>
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" className="h-14 w-14 press" onClick={() => step(-0.5)} data-testid="quick-price-minus"><Minus /></Button>
          <Input value={v} onChange={(e) => setV(e.target.value)} inputMode="decimal" className="h-14 text-2xl text-center font-bold" data-testid="quick-price-input" />
          <Button type="button" variant="outline" className="h-14 w-14 press" onClick={() => step(0.5)} data-testid="quick-price-plus"><Plus /></Button>
        </div>
        <Button className="h-14 text-base press" onClick={() => onSave(parsePrice(v))} data-testid="quick-price-save">Salvar preço</Button>
      </DialogContent>
    </Dialog>
  );
}

export default function ProductsManager({ categories: initialCats }) {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState(initialCats);
  const [editing, setEditing] = useState(null);
  const [quick, setQuick] = useState(null);
  const [newCat, setNewCat] = useState("");

  const load = async () => {
    try { const { data } = await api.get("/adegas/mine/products"); setProducts(data); } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async (body, id) => {
    try {
      if (id) await api.patch(`/adegas/mine/products/${id}`, body); else await api.post("/adegas/mine/products", body);
      toast.success(id ? "Produto atualizado." : "Produto adicionado.");
      setEditing(null); setQuick(null);
      await load();
      return true;
    } catch (e) { toast.error(formatApiError(e)); return false; }
  };
  const remove = async (p) => {
    if (!window.confirm(`Excluir "${p.name}"?`)) return;
    try { await api.delete(`/adegas/mine/products/${p.product_id}`); await load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const addCategory = async (e) => {
    e.preventDefault();
    try { const { data } = await api.post("/adegas/mine/categories", { name: newCat }); setCategories(data.categories); setNewCat(""); toast.success("Categoria criada."); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-5" data-testid="products-manager">
      <div className="rounded-2xl bg-card border border-white/10 p-4">
        <div className="text-sm font-semibold mb-3">Novo produto</div>
        <ProductForm key={categories.length} categories={categories} onSubmit={(b) => save(b)} />
        <form onSubmit={addCategory} className="flex gap-2 mt-4">
          <Input value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="Criar categoria própria" minLength={2} maxLength={40} data-testid="new-category-input" />
          <Button type="submit" variant="outline" className="press" data-testid="new-category-button">Criar</Button>
        </form>
      </div>
      <div>
        <div className="text-sm font-semibold mb-2">Catálogo ({products.length})</div>
        {products.length === 0 && <p className="text-xs text-muted-foreground">Nenhum produto ainda. Adicione o primeiro acima.</p>}
        <ul className="space-y-2">
          {products.map((p) => <ProductRow key={p.product_id} p={p} onPatch={(x, patch) => save(patch, x.product_id)} onEdit={setEditing} onDelete={remove} onQuickPrice={setQuick} />)}
        </ul>
      </div>
      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent className="z-[1600] bg-card border-white/10 max-h-[90dvh] overflow-y-auto" data-testid="product-edit-dialog">
          <DialogHeader><DialogTitle>Editar produto</DialogTitle></DialogHeader>
          {editing && <ProductForm initial={editing} categories={categories} submitLabel="Salvar" onSubmit={(b) => save(b, editing.product_id)} />}
        </DialogContent>
      </Dialog>
      {quick && <QuickPrice p={quick} onClose={() => setQuick(null)} onSave={(price) => save({ price }, quick.product_id)} />}
    </div>
  );
}
